import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { placeOf, mannerOf } from './data';
import { useT, useL } from '../../lib/lessonI18n';

export const STYLES = `@import url('https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;700;900&display=swap');.ipa{font-family:'Noto Sans','Doulos SIL','Charis SIL','Gentium Plus','DejaVu Sans',sans-serif}
.vt-reveal{opacity:0;transform:translateY(22px);transition:opacity .7s ease,transform .7s cubic-bezier(.2,.7,.2,1)}.vt-reveal.vt-in{opacity:1;transform:none}
@keyframes ctPulse{0%,100%{opacity:.55;transform:scale(.85)}50%{opacity:1;transform:scale(1.15)}}.ct-pulse{transform-box:fill-box;transform-origin:center;animation:ctPulse 1.4s ease-in-out infinite}
@keyframes ctBuzz{0%,100%{transform:translateY(-.6px)}50%{transform:translateY(.6px)}}.ct-buzz{animation:ctBuzz .08s linear infinite}
@keyframes ctFlow{to{stroke-dashoffset:-12}}.ct-flow{animation:ctFlow .6s linear infinite}
@keyframes ctFoldL{0%,100%{transform:rotate(0)}50%{transform:rotate(3deg)}}.ct-fold-l{animation:ctFoldL .12s linear infinite}
@keyframes ctFoldR{0%,100%{transform:rotate(0)}50%{transform:rotate(-3deg)}}.ct-fold-r{animation:ctFoldR .12s linear infinite}
@keyframes ctWave{to{transform:translateX(-16px)}}.ct-wave{animation:ctWave .5s linear infinite}
@keyframes ctRelease{0%,55%{transform:translateY(0)}70%,100%{transform:translateY(7px)}}.ct-release{animation:ctRelease 1.6s ease-in-out infinite}
@keyframes ctBurst{0%,60%{opacity:0;transform:scale(.3)}75%{opacity:1}100%{opacity:0;transform:scale(2.4)}}.ct-burst{transform-box:fill-box;transform-origin:center;animation:ctBurst 1.6s ease-out infinite}
@keyframes ctTap{0%,40%,100%{transform:translateY(0)}50%{transform:translateY(10px)}60%{transform:translateY(0)}}.ct-tap{animation:ctTap 1.2s ease-in-out infinite}
@keyframes ctGrow{from{transform:scaleY(0)}to{transform:scaleY(1)}}.ct-grow{transform-origin:bottom;animation:ctGrow .8s cubic-bezier(.2,.7,.2,1) both}
@media (prefers-reduced-motion:reduce){.vt-reveal{opacity:1;transform:none;transition:none}[class*="ct-"]{animation:none!important}}`;

export const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.1 });
    io.observe(el); return () => io.disconnect();
  }, []);
  return <div ref={ref} className={`vt-reveal ${shown ? 'vt-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
};

export const Kicker = ({ children }) => <p className="text-[11px] uppercase tracking-[0.3em] text-orange-400 font-bold mb-4">{children}</p>;

export const Head = ({ kicker, title, sub }) => (
  <Reveal className="mb-8 text-center">
    <Kicker>{kicker}</Kicker>
    <h2 className="text-3xl font-black tracking-tight sm:text-4xl">{title}</h2>
    {sub && <p className="mx-auto mt-3 max-w-2xl text-slate-400">{sub}</p>}
  </Reveal>
);

export const Badge = ({ label, value, tone = 'cyan' }) => (
  <div className={`rounded-lg border px-2.5 py-1.5 ${tone === 'orange' ? 'border-orange-400/30 bg-orange-500/5' : tone === 'lime' ? 'border-lime-400/30 bg-lime-500/5' : 'border-cyan-500/20 bg-cyan-500/5'}`}>
    <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`text-xs font-bold ${tone === 'orange' ? 'text-orange-200' : tone === 'lime' ? 'text-lime-200' : 'text-cyan-100'}`}>{value}</div>
  </div>
);

export const toneOf = (c) => (c.sonorant ? 'lime' : c.voice ? 'orange' : 'cyan');
export const chipCls = { lime: 'bg-lime-500/15 text-lime-300', orange: 'bg-orange-500/15 text-orange-300', cyan: 'bg-cyan-500/15 text-cyan-200' };

export const ConsonantCard = ({ c, open, onToggle }) => {
  const T = useT();
  const L = useL();
  const tone = toneOf(c);
  return (
    <div id={`ct-${c.id}`} data-testid={`consonant-card-${c.id}`}
      className={`scroll-mt-24 rounded-2xl border transition-all duration-300 ${open ? 'border-orange-400/60 bg-slate-900/80 shadow-[0_0_34px_-12px_rgba(251,146,60,0.5)]' : 'border-cyan-500/15 bg-slate-900/50 hover:border-cyan-400/40'}`}>
      <button onClick={onToggle} className="flex w-full items-center gap-4 px-5 py-4 text-left" data-testid={`consonant-card-toggle-${c.id}`}>
        <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl text-2xl font-black ${chipCls[tone]}`}>{c.ipa}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-bold text-white">{c.key}</span>
          <span className="block truncate text-xs uppercase tracking-wider text-slate-400">{L(placeOf(c.place).label)} · {L(mannerOf(c.manner).label)} · {c.voice ? T('sonora', 'voiced') : T('sorda', 'voiceless')}</span>
        </span>
        <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform duration-300 ${open ? 'rotate-180 text-orange-300' : ''}`} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <div className="space-y-4 px-5 pb-5 pt-1">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Badge label={T('Luogo', 'Place')} value={L(placeOf(c.place).label)} />
              <Badge label={T('Modo', 'Manner')} value={L(mannerOf(c.manner).label)} />
              <Badge label={T('Sonorità', 'Voicing')} value={c.voice ? T('Sonora', 'Voiced') : T('Sorda', 'Voiceless')} tone={c.voice ? 'orange' : 'cyan'} />
              <Badge label={T('Classe', 'Class')} value={c.sonorant ? T('Sonorante', 'Sonorant') : c.fortis ? T('Ostruente · fortis', 'Obstruent · fortis') : T('Ostruente · lenis', 'Obstruent · lenis')} tone={c.sonorant ? 'lime' : 'cyan'} />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-300/70">{T('Articolazione', 'Articulation')}</div>
              <p className="mt-1 text-sm leading-relaxed text-slate-200">{L(c.art)}</p>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-orange-300/80">{T('Errore comune', 'Common error')}</div>
              <p className="mt-1 text-sm leading-relaxed text-slate-300">{L(c.err)}</p>
            </div>
            <div className="rounded-xl border border-cyan-500/20 bg-slate-950/50 px-4 py-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{T('Drill · coppie minime', 'Drill · minimal pairs')}</div>
              <p className="mt-1 font-mono text-base text-cyan-100">{c.drill}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
